import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Global error boundary — catches unhandled React errors and displays
 * a recovery UI with a "Contact support" button that opens the USS widget.
 */
export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('[ErrorBoundary] Uncaught error:', error, info.componentStack);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleOpenSupport = (): void => {
    try {
      (window as any).LumenIQSupport?.open();
    } catch {
      // Widget may not be loaded — fall back to mailto
      window.location.href = 'mailto:support@ocnexus.dev';
    }
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const errorMessage = this.state.error?.message ?? 'An unexpected error occurred.';

    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100vh', gap: 16,
        background: 'var(--bg, #0a0a0f)', color: 'var(--text-dim, #d4d4d8)',
        fontFamily: 'var(--font-sans, system-ui, sans-serif)',
        padding: '0 24px', textAlign: 'center',
      }}>
        <AlertTriangle size={32} style={{ color: 'var(--red, #ef4444)' }} />

        <h1 style={{
          fontSize: 'var(--text-lg, 18px)', fontWeight: 600,
          color: 'var(--text, #fafafa)', margin: 0,
        }}>
          Something went wrong
        </h1>

        <p style={{
          fontSize: 'var(--text-sm, 14px)', color: 'var(--text-muted, #71717a)',
          maxWidth: 480, lineHeight: 1.6, margin: 0,
        }}>
          The application encountered an unexpected error. You can try reloading the page,
          or contact support if the issue persists.
        </p>

        {/* Error detail */}
        <div style={{
          padding: '10px 14px', background: 'var(--surface2, #1a1a2e)',
          borderRadius: 'var(--radius-md, 8px)', border: '1px solid var(--border, #27272a)',
          fontSize: 'var(--text-xs, 12px)', fontFamily: 'var(--font-mono, monospace)',
          color: 'var(--red, #ef4444)', maxWidth: 480, wordBreak: 'break-word',
          textAlign: 'left',
        }}>
          {errorMessage.substring(0, 300)}
        </div>

        <p style={{
          fontSize: 'var(--text-xs, 12px)', color: 'var(--text-muted, #71717a)',
          maxWidth: 480, lineHeight: 1.5, margin: 0,
        }}>
          When contacting support, include the error message above and any pipeline ID
          or page you were on when the error occurred.
        </p>

        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button
            onClick={this.handleReload}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', fontSize: 'var(--text-sm, 14px)',
              background: 'var(--surface2, #1a1a2e)', color: 'var(--text, #fafafa)',
              border: '1px solid var(--border, #27272a)',
              borderRadius: 'var(--radius-md, 8px)', cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <RefreshCw size={14} /> Reload page
          </button>
          <button
            onClick={this.handleOpenSupport}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', fontSize: 'var(--text-sm, 14px)',
              background: 'var(--red, #ef4444)', color: '#fff',
              border: 'none', borderRadius: 'var(--radius-md, 8px)',
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Contact support
          </button>
        </div>
      </div>
    );
  }
}
